import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  Eye,
  MousePointerClick,
  ShieldCheck,
  AlertTriangle,
  Link2,
  Users,
  Video,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import {
  getEditionSummary,
  getEditionSubscribers,
  EditionSummary,
  EditionSubscribersResponse,
  EditionSubscriberRow,
} from "../services/analyticsService";
import { ReactComponent as InfoIcon } from "../assets/icons/Info.svg";

const statusBadgeColors: Record<string, { bg: string; color: string }> = {
  Clicked: { bg: "#dbeafe", color: "#1e40af" },
  Opened: { bg: "#d1fae5", color: "#065f46" },
  Delivered: { bg: "#e0e7ff", color: "#3730a3" },
  Bounced: { bg: "#fee2e2", color: "#dc2626" },
  Unsubscribed: { bg: "#e5e7eb", color: "#374151" },
};

const editionStatusBadgeColors: Record<string, { bg: string; color: string }> = {
  Sending: { bg: "#fef3c7", color: "#92400e" },
  Published: { bg: "#d1fae5", color: "#065f46" },
};

const formatPercent = (numerator: number | undefined | null, denominator: number | undefined | null): string => {
  const n = numerator ?? 0;
  const d = denominator ?? 0;
  if (d === 0) return "0%";
  return (n / d * 100).toFixed(2) + "%";
};

const formatNumber = (n: number | undefined | null): string => (n ?? 0).toLocaleString();

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  padding: "20px",
};

const cardLabelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#718096",
  fontSize: "13px",
  fontWeight: 500,
  marginBottom: "8px",
};

const cardValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 700,
  color: "#1a202c",
};

const cardSubStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "#a0aec0",
  marginTop: "4px",
};

const SkeletonCard = () => (
  <div style={{ ...cardStyle, height: "110px" }}>
    <div style={{ width: "60%", height: "14px", background: "#e2e8f0", borderRadius: "4px", marginBottom: "16px" }} />
    <div style={{ width: "40%", height: "28px", background: "#e2e8f0", borderRadius: "4px" }} />
  </div>
);

const VERIFIED_CLICKS_TOOLTIP =
  "Verified clicks use a proprietary formula to confirm that a subscriber has actually clicked. This represents a lower bound on real clicks. Email security systems at universities, large companies, and other organizations may automatically click links, which can make it impossible to detect who actually clicked.";

const EditionAnalytics: React.FC = () => {
  const { editionId } = useParams<{ editionId: string }>();
  const navigate = useNavigate();

  const [summary, setSummary] = useState<EditionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [subscribers, setSubscribers] = useState<EditionSubscribersResponse | null>(null);
  const [subsLoading, setSubsLoading] = useState(true);
  const [subsPage, setSubsPage] = useState(1);
  const [subsSearch, setSubsSearch] = useState("");
  const [subsStatusFilter, setSubsStatusFilter] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expandedLink, setExpandedLink] = useState<string | null>(null);
  const [linkClickers, setLinkClickers] = useState<EditionSubscriberRow[] | null>(null);
  const [linkClickersLoading, setLinkClickersLoading] = useState(false);
  const [linkClickersTotal, setLinkClickersTotal] = useState(0);
  const [linkClickersPage, setLinkClickersPage] = useState(1);

  // Fetch summary
  useEffect(() => {
    if (!editionId) return;
    setSummaryLoading(true);
    setSummaryError(null);
    getEditionSummary(editionId)
      .then(setSummary)
      .catch((e) => setSummaryError(e.response?.data?.error || "Failed to load analytics"))
      .finally(() => setSummaryLoading(false));
  }, [editionId]);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(subsSearch);
      setSubsPage(1);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [subsSearch]);

  // Fetch link clickers when a link is expanded
  useEffect(() => {
    if (!editionId || !expandedLink) {
      setLinkClickers(null);
      return;
    }
    setLinkClickersLoading(true);
    getEditionSubscribers(editionId, linkClickersPage, 10, undefined, undefined, expandedLink)
      .then((res) => {
        setLinkClickers(res.subscribers);
        setLinkClickersTotal(res.total_count);
      })
      .catch(() => setLinkClickers(null))
      .finally(() => setLinkClickersLoading(false));
  }, [editionId, expandedLink, linkClickersPage]);

  // Fetch subscribers
  const fetchSubscribers = useCallback(() => {
    if (!editionId) return;
    setSubsLoading(true);
    getEditionSubscribers(
      editionId,
      subsPage,
      20,
      debouncedSearch || undefined,
      subsStatusFilter || undefined
    )
      .then(setSubscribers)
      .catch(() => setSubscribers(null))
      .finally(() => setSubsLoading(false));
  }, [editionId, subsPage, debouncedSearch, subsStatusFilter]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const getVisiblePages = (currentPage: number, totalPages: number) => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = startPage + maxVisible - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = endPage - maxVisible + 1;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const overview = summary?.overview;

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            color: "#6b7280",
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#1a202c", margin: 0 }}>
          {summaryLoading ? "Loading..." : summary?.edition.title || "Edition Analytics"}
        </h1>
        {summary?.edition.status && (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
              backgroundColor: editionStatusBadgeColors[summary.edition.status]?.bg || "#e5e7eb",
              color: editionStatusBadgeColors[summary.edition.status]?.color || "#374151",
            }}
          >
            {summary.edition.status}
          </span>
        )}
      </div>
      <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 12px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", color: "#1e40af", fontSize: "13px", fontWeight: 500, cursor: "help", whiteSpace: "nowrap" }}>
        <InfoIcon style={{ width: "14px", height: "14px" }} />
        Struggling to understand email metrics?
        <div className="tooltip" style={{ width: "340px", whiteSpace: "normal", textAlign: "left", fontWeight: 400, right: 0, left: "auto", transform: "none" }}>
          Email open and click metrics can be misleading due to bot scanning and tracking pixel blocking. Reach out to <a href="mailto:team@withpotions.com" style={{ color: "#1e40af", fontWeight: 600 }}>team@withpotions.com</a> and we'll help you make sense of your data.
        </div>
      </div>
      </div>

      {summary?.edition.scheduled_send_start_date && (
        <div style={{ fontSize: "14px", color: "#718096", marginBottom: "24px", marginLeft: "36px" }}>
          Sent {formatDate(summary.edition.scheduled_send_start_date)}
        </div>
      )}

      {/* Warning banner for still-sending editions */}
      {summary?.edition.status === "Sending" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 16px",
            backgroundColor: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: "8px",
            marginBottom: "24px",
            fontSize: "14px",
            color: "#92400e",
          }}
        >
          <AlertTriangle size={18} />
          This edition is still being sent. Analytics are still being collected and may take several
          days to be complete.
        </div>
      )}

      {summaryError && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#fee2e2",
            border: "1px solid #fca5a5",
            borderRadius: "8px",
            marginBottom: "24px",
            fontSize: "14px",
            color: "#dc2626",
          }}
        >
          {summaryError}
        </div>
      )}

      {/* Overview Stat Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        {summaryLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : overview ? (
          <>
            <div style={cardStyle}>
              <div style={cardLabelStyle}>
                <Send size={16} />
                Delivered
              </div>
              <div style={cardValueStyle}>{formatNumber(overview.delivered)}</div>
              <div style={cardSubStyle}>
                {formatNumber(overview.bounced)} bounced
              </div>
            </div>

            <div style={cardStyle}>
              <div style={cardLabelStyle}>
                <Eye size={16} />
                Open Rate
              </div>
              <div style={cardValueStyle}>
                {formatPercent(overview.unique_opened, overview.delivered)}
              </div>
              <div style={cardSubStyle}>
                {formatNumber(overview.unique_opened)} unique opens
              </div>
              <div style={cardSubStyle}>
                {formatNumber(overview.opened)} total opens
              </div>
            </div>

            <div style={cardStyle}>
              <div style={cardLabelStyle}>
                <ShieldCheck size={16} />
                Verified Clicks
                <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.4, cursor: "help" }} />
                  <div className="tooltip">
                    {VERIFIED_CLICKS_TOOLTIP}
                  </div>
                </div>
              </div>
              <div style={cardValueStyle}>
                {formatNumber(overview.verified_total_clicks)}
              </div>
              <div style={cardSubStyle}>
                {formatNumber(overview.verified_unique_clicks)} verified unique clicks
              </div>
              <div style={cardSubStyle}>
                {formatNumber(overview.clicked)} total clicks
              </div>
            </div>

            <div style={cardStyle}>
              <div style={cardLabelStyle}>
                <MousePointerClick size={16} />
                Click-to-Open Rate
                <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.4, cursor: "help" }} />
                  <div className="tooltip">
                    Percentage of subscribers who clicked at least one link, out of those who opened
                    the email.
                  </div>
                </div>
              </div>
              <div style={cardValueStyle}>
                {formatPercent(overview.unique_clicked, overview.unique_opened)}
              </div>
              <div style={cardSubStyle}>
                {formatNumber(overview.unique_clicked)} unique clicks
              </div>
            </div>

            <div style={cardStyle}>
              <div style={cardLabelStyle}>
                <Users size={16} />
                Unsubscribe Rate
              </div>
              <div style={cardValueStyle}>
                {formatPercent(overview.unsubscribed, overview.delivered)}
              </div>
              <div style={cardSubStyle}>
                {formatNumber(overview.unsubscribed)} unsubscribes
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Link Performance Table */}
      {summary && summary.link_clicks.length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "24px",
            marginBottom: "32px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <Link2 size={18} color="#718096" />
            <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#1a202c", margin: 0 }}>
              Link Performance
            </h2>
            <span style={{ fontSize: "13px", color: "#a0aec0" }}>
              {summary.link_clicks.length} links tracked
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    URL
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Total Clicks
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      Verified Total
                      <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                        <InfoIcon style={{ width: "13px", height: "13px", opacity: 0.4, cursor: "help" }} />
                        <div className="tooltip" style={{ width: "280px", textAlign: "left" }}>
                          {VERIFIED_CLICKS_TOOLTIP}
                        </div>
                      </div>
                    </div>
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Unique Clicks
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      Verified Unique
                      <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                        <InfoIcon style={{ width: "13px", height: "13px", opacity: 0.4, cursor: "help" }} />
                        <div className="tooltip" style={{ width: "280px", textAlign: "left" }}>
                          {VERIFIED_CLICKS_TOOLTIP}
                        </div>
                      </div>
                    </div>
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Click-to-Open Rate
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Verified Click-to-Open
                  </th>
                  <th style={{ textAlign: "center", padding: "10px 12px", color: "#718096", fontWeight: 500, width: "100px" }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.link_clicks.map((link, idx) => {
                  const isExpanded = expandedLink === link.original_link;
                  return (
                    <React.Fragment key={idx}>
                      <tr style={{ borderBottom: isExpanded ? "none" : "1px solid #f1f5f9" }}>
                        <td
                          style={{
                            padding: "10px 12px",
                            maxWidth: "400px",
                            wordBreak: "break-all",
                            color: "#5d5ded",
                          }}
                        >
                          <a
                            href={link.original_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#5d5ded", textDecoration: "none" }}
                          >
                            {link.original_link.length > 80
                              ? link.original_link.substring(0, 80) + "..."
                              : link.original_link}
                          </a>
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 12px" }}>
                          {formatNumber(link.total_clicks)}
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 12px" }}>
                          {formatNumber(link.verified_total_clicks)}
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 12px" }}>
                          {formatNumber(link.unique_clicks)}
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 12px" }}>
                          {formatNumber(link.verified_unique_clicks)}
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 12px" }}>
                          {overview ? formatPercent(link.unique_clicks, overview.unique_opened) : "-"}
                        </td>
                        <td style={{ textAlign: "right", padding: "10px 12px" }}>
                          {overview ? formatPercent(link.verified_unique_clicks, overview.unique_opened) : "-"}
                        </td>
                        <td style={{ textAlign: "center", padding: "10px 12px" }}>
                          <button
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedLink(null);
                              } else {
                                setExpandedLink(link.original_link);
                                setLinkClickersPage(1);
                              }
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#5d5ded",
                              fontSize: "13px",
                              padding: "4px 8px",
                              borderRadius: "4px",
                            }}
                          >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {isExpanded ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} style={{ padding: "0 12px 16px 12px", backgroundColor: "#f9fafb" }}>
                            <div style={{ padding: "12px 16px", borderRadius: "6px" }}>
                              <div style={{ fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                                Subscribers who clicked this link
                                {linkClickersTotal > 0 && (
                                  <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: "8px" }}>
                                    {linkClickersTotal} subscriber{linkClickersTotal !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                              {linkClickersLoading && !linkClickers ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#a0aec0", padding: "12px 0" }}>
                                  <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                                  Loading...
                                </div>
                              ) : linkClickers && linkClickers.length > 0 ? (
                                <>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                      <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#718096", fontWeight: 500 }}>Email</th>
                                        <th style={{ textAlign: "right", padding: "6px 8px", color: "#718096", fontWeight: 500 }}>Clicks</th>
                                        <th style={{ textAlign: "right", padding: "6px 8px", color: "#718096", fontWeight: 500 }}>Verified</th>
                                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#718096", fontWeight: 500 }}>Last Clicked</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {linkClickers.map((clicker) => (
                                        <tr key={clicker.subscriber_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                          <td style={{ padding: "6px 8px" }}>
                                            <a
                                              href={`/subscribers/${clicker.subscriber_id}`}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`/subscribers/${clicker.subscriber_id}`);
                                              }}
                                              style={{ color: "#5d5ded", textDecoration: "none" }}
                                            >
                                              {clicker.email}
                                            </a>
                                          </td>
                                          <td style={{ textAlign: "right", padding: "6px 8px" }}>{clicker.total_clicks}</td>
                                          <td style={{ textAlign: "right", padding: "6px 8px" }}>{clicker.verified_clicks}</td>
                                          <td style={{ padding: "6px 8px", color: "#718096" }}>{formatDate(clicker.latest_event_at)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {linkClickersTotal > 10 && (
                                    <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "8px" }}>
                                      <button
                                        onClick={() => setLinkClickersPage(linkClickersPage - 1)}
                                        disabled={linkClickersPage === 1}
                                        style={{
                                          padding: "4px 10px",
                                          fontSize: "12px",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "4px",
                                          background: "#fff",
                                          cursor: linkClickersPage === 1 ? "not-allowed" : "pointer",
                                          color: linkClickersPage === 1 ? "#cbd5e0" : "#374151",
                                        }}
                                      >
                                        Previous
                                      </button>
                                      <span style={{ fontSize: "12px", color: "#718096", padding: "4px 0" }}>
                                        Page {linkClickersPage} of {Math.ceil(linkClickersTotal / 10)}
                                      </span>
                                      <button
                                        onClick={() => setLinkClickersPage(linkClickersPage + 1)}
                                        disabled={linkClickersPage >= Math.ceil(linkClickersTotal / 10)}
                                        style={{
                                          padding: "4px 10px",
                                          fontSize: "12px",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "4px",
                                          background: "#fff",
                                          cursor: linkClickersPage >= Math.ceil(linkClickersTotal / 10) ? "not-allowed" : "pointer",
                                          color: linkClickersPage >= Math.ceil(linkClickersTotal / 10) ? "#cbd5e0" : "#374151",
                                        }}
                                      >
                                        Next
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div style={{ color: "#a0aec0", padding: "8px 0", fontSize: "13px" }}>
                                  No clicker data available.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Subscriber Engagement */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Users size={18} color="#718096" />
            <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#1a202c", margin: 0 }}>
              Subscriber Engagement
            </h2>
            {subscribers && (
              <span style={{ fontSize: "13px", color: "#a0aec0" }}>
                {subscribers.total_count} subscribers
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              placeholder="Search emails"
              value={subsSearch}
              onChange={(e) => setSubsSearch(e.target.value)}
              style={{
                padding: "6px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                width: "200px",
                outline: "none",
              }}
            />
            <select
              value={subsStatusFilter}
              onChange={(e) => {
                setSubsStatusFilter(e.target.value);
                setSubsPage(1);
              }}
              style={{
                padding: "6px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                outline: "none",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <option value="">All statuses</option>
              <option value="Clicked">Clicked</option>
              <option value="Opened">Opened</option>
              <option value="Delivered">Delivered</option>
              <option value="Bounced">Bounced</option>
              <option value="Unsubscribed">Unsubscribed</option>
            </select>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "10px 12px",
            backgroundColor: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "6px",
            marginBottom: "12px",
            fontSize: "13px",
            color: "#1e40af",
          }}
        >
          <InfoIcon style={{ width: "14px", height: "14px", flexShrink: 0 }} />
          Total clicks per subscriber can be inflated due to link scanning mechanisms employed by
          organizations and email service providers.
        </div>

        {subsLoading && !subscribers ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}>
            Loading subscribers...
          </div>
        ) : subscribers && subscribers.subscribers.length > 0 ? (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Email
                  </th>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Status
                  </th>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Latest Event
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Total Opens
                  </th>
                  <th style={{ textAlign: "right", padding: "10px 12px", color: "#718096", fontWeight: 500 }}>
                    Total Clicks
                  </th>
                </tr>
              </thead>
              <tbody>
                {subscribers.subscribers.map((sub) => {
                  const badge = statusBadgeColors[sub.engagement_status] || {
                    bg: "#e5e7eb",
                    color: "#374151",
                  };
                  return (
                    <tr key={sub.subscriber_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <a
                          href={`/subscribers/${sub.subscriber_id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/subscribers/${sub.subscriber_id}`);
                          }}
                          style={{ color: "#5d5ded", textDecoration: "none" }}
                        >
                          {sub.email}
                        </a>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: "10px",
                            fontSize: "12px",
                            fontWeight: 600,
                            backgroundColor: badge.bg,
                            color: badge.color,
                          }}
                        >
                          {sub.engagement_status}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#718096" }}>
                        {formatDate(sub.latest_event_at)}
                      </td>
                      <td style={{ textAlign: "right", padding: "10px 12px" }}>
                        {sub.total_opens}
                      </td>
                      <td style={{ textAlign: "right", padding: "10px 12px" }}>
                        {sub.total_clicks}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {subscribers.total_pages > 1 && (
              <div style={{ textAlign: "center", margin: "20px 0" }}>
                <button
                  onClick={() => setSubsPage(1)}
                  disabled={subsPage === 1}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: subsPage === 1 ? "not-allowed" : "pointer" }}
                >
                  &lt;&lt;
                </button>
                <button
                  onClick={() => setSubsPage(subsPage - 1)}
                  disabled={subsPage === 1}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: subsPage === 1 ? "not-allowed" : "pointer" }}
                >
                  &lt;
                </button>
                {getVisiblePages(subsPage, subscribers.total_pages).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setSubsPage(pageNum)}
                    className="subscriber-pagination-number-button"
                    style={{
                      backgroundColor: subsPage === pageNum ? "#5d5ded" : "#fff",
                      color: subsPage === pageNum ? "#fff" : "#5d5ded",
                    }}
                  >
                    {pageNum}
                  </button>
                ))}
                <button
                  onClick={() => setSubsPage(subsPage + 1)}
                  disabled={subsPage === subscribers.total_pages}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: subsPage === subscribers.total_pages ? "not-allowed" : "pointer" }}
                >
                  &gt;
                </button>
                <button
                  onClick={() => setSubsPage(subscribers.total_pages)}
                  disabled={subsPage === subscribers.total_pages}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: subsPage === subscribers.total_pages ? "not-allowed" : "pointer" }}
                >
                  &gt;&gt;
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}>
            No subscriber engagement data found.
          </div>
        )}
      </div>
    </div>
  );
};

export default EditionAnalytics;
